import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CashReconciliationService } from '../../cash-reconciliation.service';
import { CurrencyActionInputComponent } from '../../../../../shared/components/currency-action-input/currency-action-input.component';
import { MATCH_STATUS } from '../../constants/cash-reconciliation.constants';
import { statusIsPlain, getStatusConfig } from '../../utils/cash-reconciliation.utils';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import {
  ReconciliationGridEmployee,
  ReconciliationGridEntry,
  ReconciliationGridSavePayload,
  FixReconciliationEntry,
} from '../../interfaces/cash-reconciliation.interface';

@Component({
  selector: 'app-reconciliation-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, CurrencyActionInputComponent, StatusBadgeComponent],
  templateUrl: './reconciliation-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reconciliation-grid.component.scss'],
})
export class ReconciliationGridComponent implements OnChanges {
  private readonly service = inject(CashReconciliationService);
  readonly MATCH_STATUS = MATCH_STATUS;
  readonly statusIsPlain = statusIsPlain;
  readonly getStatusConfig = getStatusConfig;

  @Input() rows: ReconciliationGridEmployee[] = [];
  @Input() savingEmployeeIds: string[] = [];
  @Input() readonly = false;
  @Input() bodyClass = '';
  @Output() saveRow = new EventEmitter<ReconciliationGridSavePayload>();
  @Output() cancelEmployee = new EventEmitter<string>();
  @Output() dirtyChange = new EventEmitter<void>();

  private originalByEntryId = new Map<string, { opening: number | null; closing: number | null }>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      this.rebuildOriginalMap();
    }
  }

  trackByEmployee(_i: number, row: ReconciliationGridEmployee): string {
    return row.employee_id;
  }

  trackByEntry(_i: number, e: ReconciliationGridEntry): string {
    return e.id;
  }

  isSaving(employeeId: string): boolean {
    return this.savingEmployeeIds.includes(employeeId);
  }

  formatCurrency(value: number | null, opts?: { signed?: boolean }): string {
    if (value == null) {
      return '---';
    }
    const s = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
    if (opts?.signed && value > 0) {
      return `+${s}`;
    }
    return s;
  }

  deltaClass(
    entry: ReconciliationGridEntry
  ): 'rg-delta--zero-pos' | 'rg-delta--neg' | 'rg-delta--plain' {
    const v = entry.delta;
    if (v == null) {
      return 'rg-delta--plain';
    }
    return v < 0 ? 'rg-delta--neg' : 'rg-delta--zero-pos';
  }

  onEntryFieldChange(_entry: ReconciliationGridEntry): void {
    this.dirtyChange.emit();
  }

  recomputeFromInputs(entry: FixReconciliationEntry) {
    const updated = this.service.recomputeFixEntry(entry);

    Object.assign(entry, updated);
  }

  hasChanges(row: ReconciliationGridEmployee): boolean {
    return this.buildChanges(row).some(change => {
      const hasValidOpening =
        change.opening_balance !== undefined
          ? change.opening_balance != null && change.opening_balance > 0
          : true;

      const hasValidClosing =
        change.closing_balance !== undefined
          ? change.closing_balance != null && change.closing_balance > 0
          : true;

      return hasValidOpening && hasValidClosing;
    });
  }

  submitSave(row: ReconciliationGridEmployee): void {
    if (this.readonly) return;

    const data = this.buildChanges(row);
    if (data.length === 0) {
      return;
    }
    this.saveRow.emit({
      employeeId: row.employee_id,
      data,
    });
  }

  cancelFor(row: ReconciliationGridEmployee): void {
    if (this.readonly) return;
    this.cancelEmployee.emit(row.employee_id);
  }

  private rebuildOriginalMap(): void {
    this.originalByEntryId.clear();
    for (const row of this.rows) {
      for (const entry of row.entries) {
        this.originalByEntryId.set(entry.id, {
          opening: entry.opening_balance,
          closing: entry.closing_balance,
        });
      }
    }
  }

  private buildChanges(row: ReconciliationGridEmployee): {
    id: string;
    opening_balance?: number | null;
    closing_balance?: number | null;
  }[] {
    const changes: {
      id: string;
      opening_balance?: number | null;
      closing_balance?: number | null;
    }[] = [];

    for (const entry of row.entries) {
      const original = this.originalByEntryId.get(entry.id);
      if (!original || !entry.is_editable) {
        continue;
      }

      const next: {
        id: string;
        opening_balance?: number | null;
        closing_balance?: number | null;
      } = { id: entry.id };

      if (entry.opening_balance !== original.opening) {
        next.opening_balance = entry.opening_balance;
      }

      if (entry.closing_balance !== original.closing) {
        next.closing_balance = entry.closing_balance;
      }

      if (next.opening_balance !== undefined || next.closing_balance !== undefined) {
        changes.push(next);
      }
    }

    return changes;
  }

  onOpeningChange(entry: ReconciliationGridEntry, value: number | null): void {
    entry.opening_balance = value; //ensure latest value
    this.recomputeFromInputs(entry);
  }

  onClosingChange(entry: ReconciliationGridEntry, value: number | null): void {
    entry.closing_balance = value;
    this.recomputeFromInputs(entry);
  }
}
